import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react'
import { useReactFlow, useStore } from '@xyflow/react'
import { JourneyNode, JourneyEdge } from './types/journey-nodes'

const MIN_AUTO_ZOOM = 0.4
const MAX_AUTO_ZOOM = 1
const TOP_PADDING = 40
const BOTTOM_PADDING = 80
// How long to ignore viewport changes after a programmatic setViewport call.
// Must be longer than the animation duration (200ms) to avoid false positives.
const PROGRAMMATIC_SETTLE_MS = 300

export type ViewportAdjusterRef = {
    fitView: () => void
}

type ViewportAdjusterProps = {
    nodes: JourneyNode[]
    edges?: JourneyEdge[]
}

/**
 * Computes the viewport (x, y, zoom) that fits all nodes and edge bend points.
 * Centers horizontally, positions content at the top.
 */
function computeFitViewport(
    nodes: JourneyNode[],
    edges: JourneyEdge[] | undefined,
    containerWidth: number,
    containerHeight: number,
): { x: number; y: number; zoom: number } | null {
    if (!nodes.length || containerWidth === 0 || containerHeight === 0) return null

    let minX = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    nodes.forEach((node) => {
        if (node.hidden) return
        const x = node.position.x
        const y = node.position.y
        const width = node.measured?.width ?? 250
        const height = node.measured?.height ?? 58
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x + width)
        maxY = Math.max(maxY, y + height)
    })

    // Expand bounds with edge routing bend points (split edge lanes extend beyond nodes)
    if (edges) {
        for (const edge of edges) {
            const sections = (edge.data as any)?.elkSections
            if (!sections) continue
            for (const section of sections) {
                for (const pt of [section.startPoint, ...(section.bendPoints ?? []), section.endPoint]) {
                    if (pt) {
                        minX = Math.min(minX, pt.x)
                        maxX = Math.max(maxX, pt.x)
                        maxY = Math.max(maxY, pt.y)
                    }
                }
            }
        }
    }

    if (minX === Infinity) return null

    const contentWidth = maxX - minX
    const contentHeight = maxY - 0

    const availableWidth = containerWidth - TOP_PADDING * 2
    const availableHeight = containerHeight - TOP_PADDING - BOTTOM_PADDING

    const zoomX = availableWidth / contentWidth
    const zoomY = availableHeight / contentHeight
    const fitZoom = Math.min(zoomX, zoomY)
    const zoom = Math.max(MIN_AUTO_ZOOM, Math.min(MAX_AUTO_ZOOM, fitZoom))

    const scaledContentWidth = contentWidth * zoom
    const centerX = (containerWidth - scaledContentWidth) / 2 - minX * zoom
    const topY = TOP_PADDING

    return { x: centerX, y: topY, zoom }
}


/**
 * Automatically adjusts the React Flow viewport after layout changes.
 *
 * - Computes the zoom level needed to fit all content (clamped to [MIN_AUTO_ZOOM, 1])
 * - Centers horizontally, positions content at the top
 * - Includes edge routing bend points in bounds calculation so split variant
 *   lanes that extend beyond node positions are accounted for
 * - Respects user interaction: if the user manually zooms/pans, auto-adjustment
 *   is suppressed until the next structural layout change (node add/remove)
 * - Exposes a `fitView` method via ref for the Controls fit-view button
 *
 * Must be rendered inside ReactFlowProvider.
 */
export const ViewportAdjuster = forwardRef<ViewportAdjusterRef, ViewportAdjusterProps>(
    ({ nodes, edges }, ref) => {
        const { setViewport } = useReactFlow()
        const isInitialRender = useRef(true)
        const userHasInteracted = useRef(false)
        const lastProgrammaticMoveAt = useRef(0)

        const { width: containerWidth, height: containerHeight } = useStore((state) => ({
            width: state.width,
            height: state.height,
        }))

        // Subscribe to viewport transform to detect user-initiated changes.
        const storeViewport = useStore((state) => state.transform)
        const prevViewportRef = useRef(storeViewport)

        useEffect(() => {
            const [x, y, zoom] = storeViewport
            const [px, py, pz] = prevViewportRef.current
            prevViewportRef.current = storeViewport

            if (isInitialRender.current) return

            if (x !== px || y !== py || zoom !== pz) {
                const timeSinceProgrammatic = Date.now() - lastProgrammaticMoveAt.current
                if (timeSinceProgrammatic > PROGRAMMATIC_SETTLE_MS) {
                    userHasInteracted.current = true
                }
            }
        }, [storeViewport])

        // Build a fingerprint of node positions so we only re-adjust when layout changes.
        const positionFingerprint = useStore(
            (state) =>
                state.nodes
                    .map((n) => `${n.id}:${Math.round(n.position.x)},${Math.round(n.position.y)}`)
                    .sort()
                    .join('|'),
        )

        // Structural fingerprint (just IDs) to detect when nodes are added/removed.
        const structureFingerprint = useStore(
            (state) => state.nodes.map((n) => n.id).sort().join(','),
        )

        const prevStructureRef = useRef(structureFingerprint)

        // Reset user interaction flag when the node structure changes
        useEffect(() => {
            if (structureFingerprint !== prevStructureRef.current) {
                prevStructureRef.current = structureFingerprint
                userHasInteracted.current = false
            }
        }, [structureFingerprint])

        // Expose fitView for the Controls button
        const applyFitView = (duration: number) => {
            const vp = computeFitViewport(nodes, edges, containerWidth, containerHeight)
            if (!vp) return
            lastProgrammaticMoveAt.current = Date.now()
            setViewport(vp, { duration })
        }

        useImperativeHandle(ref, () => ({
            fitView: () => {
                userHasInteracted.current = false
                applyFitView(200)
            },
        }))

        // Main auto-zoom effect
        useEffect(() => {
            if (!nodes.length || containerWidth === 0 || containerHeight === 0) return
            if (userHasInteracted.current) return

            const duration = isInitialRender.current ? 0 : 200
            applyFitView(duration)
            isInitialRender.current = false
        }, [positionFingerprint, nodes.length, edges, containerWidth, containerHeight, setViewport])

        return null
    },
)
